import styled from '@emotion/styled';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import Button from '@shared/components/core/button/CoreButton';
import { CardBorder } from '@shared/components/core/card/CommonCardStyles';
import StyledTextField from '@shared/components/core/field/StyledTextField';
import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import Icons from '@shared/components/core/images/Icons';
import SpacedLoader from '@shared/components/loading/SpacedLoader';
import { BodyMedium, BodySmall, TitleSmall, TitleXSmall } from '@shared/components/core/text/Typography';
import SkillEditor from '@shared/components/extensions/editors/SkillEditor';
import McpServerEditor from '@shared/components/extensions/editors/McpServerEditor';
import * as skillsApi from '@shared/api/skills/skillsApi';
import * as mcpApi from '@shared/api/mcp/mcpServersApi';
import { createRestSkillEditorData } from '@admin/hooks/extensions/skillEditorDataRest';
import { createRestMcpServerEditorData } from '@admin/hooks/extensions/mcpServerEditorDataRest';
import { AgentSkillModel } from '@shared/models/agentExtensions/AgentSkillModel';
import { McpServerRegistrationModel } from '@shared/models/agentExtensions/McpServerRegistrationModel';
import { AgentExtensionSortBy } from '@shared/models/agentExtensions/AgentExtensionGovernanceModels';
import { DEFAULT_SKILL_ACTIVATION, SkillModel } from '@shared/models/skills/SkillModel';
import { McpServerModel } from '@shared/models/mcp/McpServerModel';
import { registrationToCreateInput } from '@shared/models/mcp/mcpRegistrationMapping';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExtensionFeedbackDialog from './ExtensionFeedbackDialog';
import { MIN_PUBLIC_RATINGS } from '@shared/components/extensions/ExtensionMarketplaceStats';
import RatingSummary from '@shared/components/core/rating/RatingSummary';

export type ExtensionKind = 'skills' | 'connectors';

/** Normalized card shape both AgentSkillModel and McpServerRegistrationModel map onto. */
interface ExtensionCard {
    id: string;
    displayName: string;
    description?: string;
    averageRating?: number;
    ratingCount?: number;
    installCount?: number;
    commentCount?: number;
    myRating?: number;
}

interface IOrgExtensionsManagerProps {
    organizationId: string;
    kind: ExtensionKind;
    /** Whether the current admin may create/edit org extensions of this kind. */
    canManage: boolean;
}

const SORT_OPTIONS: AgentExtensionSortBy[] = ['recent', 'rating', 'installs'];

export default function OrgExtensionsManager({
    organizationId,
    kind,
    canManage,
}: Readonly<IOrgExtensionsManagerProps>) {
    const { t } = useTranslation('common');
    const isSkills = kind === 'skills';

    const [items, setItems] = useState<(AgentSkillModel | McpServerRegistrationModel)[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sortBy, setSortBy] = useState<AgentExtensionSortBy>('recent');
    // Editor state: undefined = closed, null = create, model = edit.
    const [editing, setEditing] = useState<AgentSkillModel | McpServerRegistrationModel | null | undefined>(undefined);
    const [feedbackFor, setFeedbackFor] = useState<ExtensionCard | null>(null);

    const load = async () => {
        setIsLoading(true);
        try {
            const result = isSkills
                ? await skillsApi.getOrganizationSkills(organizationId, undefined, sortBy)
                : await mcpApi.getOrganizationMcpServers(organizationId, undefined, sortBy);
            if (result.resultType !== 'Ok' || !result.data) throw new Error('load failed');
            setItems(result.data);
        } catch {
            useAlertsStore.getState().showError(t('organization.governance.extensions.list.loadError'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, kind, sortBy]);

    const cards: ExtensionCard[] = items.map((item) => ({
        id: item.id,
        displayName: item.displayName,
        description: item.description,
        averageRating: item.averageRating,
        ratingCount: item.ratingCount,
        installCount: item.installCount,
        commentCount: item.commentCount,
        myRating: item.myRating,
    }));

    const onEditorDone = () => {
        setEditing(undefined);
        load();
    };

    return (
        <Wrap>
            <SubSectionRow
                title={t(
                    isSkills
                        ? 'organization.governance.extensions.skills.title'
                        : 'organization.governance.extensions.connectors.title'
                )}
                variant="small">
                {canManage && (
                    <Button $color="secondary" $size="small" startIcon="plus" onClick={() => setEditing(null)}>
                        {t(
                            isSkills
                                ? 'organization.governance.extensions.skills.create'
                                : 'organization.governance.extensions.connectors.create'
                        )}
                    </Button>
                )}
            </SubSectionRow>
            <BodyMedium>
                {t(
                    isSkills
                        ? 'organization.governance.extensions.skills.description'
                        : 'organization.governance.extensions.connectors.description'
                )}
            </BodyMedium>

            <SortRow>
                <FormControl size="small">
                    <InputLabel id={`sort-${kind}`}>{t('organization.governance.extensions.sort.label')}</InputLabel>
                    <Select
                        labelId={`sort-${kind}`}
                        label={t('organization.governance.extensions.sort.label')}
                        value={sortBy}
                        onChange={(event) => setSortBy(event.target.value as AgentExtensionSortBy)}>
                        {SORT_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                                {t(`organization.governance.extensions.sort.${option}`)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </SortRow>

            {isLoading ? (
                <SpacedLoader />
            ) : cards.length === 0 ? (
                <EmptyState>
                    <Icons name={isSkills ? 'sparkles' : 'tools'} size={32} />
                    <TitleSmall>{t('organization.governance.extensions.list.emptyTitle')}</TitleSmall>
                    <BodyMedium>{t('organization.governance.extensions.list.emptyDescription')}</BodyMedium>
                </EmptyState>
            ) : (
                <CardGrid>
                    {cards.map((card) => {
                        const source = items.find((i) => i.id === card.id)!;
                        return (
                            <ExtensionCardView key={card.id}>
                                <TitleXSmall>{card.displayName}</TitleXSmall>
                                {card.description && <CardDescription>{card.description}</CardDescription>}
                                {/* The stats row is the affordance for the feedback dialog — clicking the
                                    rating opens ratings & comments for this extension. */}
                                <CardStats
                                    averageRating={card.averageRating}
                                    ratingCount={card.ratingCount}
                                    installCount={card.installCount ?? 0}
                                    commentCount={card.commentCount ?? 0}
                                    minRatings={MIN_PUBLIC_RATINGS}
                                    compact
                                    actionLabel={t('organization.governance.extensions.feedback.open')}
                                    onClick={() => setFeedbackFor(card)}
                                />
                                {/* Only bordered off when there is an action in it — feedback moved to the
                                    stats row above, so this is empty for members who can't manage. */}
                                {canManage && (
                                    <CardActions>
                                        <Button
                                            $color="text"
                                            $size="small"
                                            startIcon="edit"
                                            onClick={() => setEditing(source)}>
                                            {t('edit')}
                                        </Button>
                                    </CardActions>
                                )}
                            </ExtensionCardView>
                        );
                    })}
                </CardGrid>
            )}

            {/* Both editors bring their own DetailContentDialog, so there is no wrapper here — nesting one
                would put a dialog inside a dialog. */}
            {editing !== undefined &&
                (isSkills ? (
                    <SkillEditor
                        editorMode={editing ? 'edit' : 'create'}
                        skill={editing ? toLocalSkill(editing as AgentSkillModel) : undefined}
                        data={createRestSkillEditorData(organizationId)}
                        onDone={onEditorDone}
                        onCancel={() => setEditing(undefined)}
                    />
                ) : (
                    <McpServerEditor
                        editorMode={editing ? 'edit' : 'create'}
                        server={editing ? toLocalServer(editing as McpServerRegistrationModel) : undefined}
                        data={createRestMcpServerEditorData(organizationId)}
                        onDone={onEditorDone}
                        onCancel={() => setEditing(undefined)}
                    />
                ))}

            {feedbackFor && (
                <ExtensionFeedbackDialog
                    organizationId={organizationId}
                    kind={kind}
                    extensionId={feedbackFor.id}
                    title={feedbackFor.displayName}
                    initialRating={feedbackFor.myRating}
                    stats={{
                        averageRating: feedbackFor.averageRating,
                        ratingCount: feedbackFor.ratingCount,
                        installCount: feedbackFor.installCount ?? 0,
                        commentCount: feedbackFor.commentCount ?? 0,
                    }}
                    onClose={() => {
                        setFeedbackFor(null);
                        load();
                    }}
                />
            )}
        </Wrap>
    );
}

/** Map the API skill read model to the local SkillModel the editor edits (body loaded lazily by id). */
function toLocalSkill(model: AgentSkillModel): SkillModel {
    return {
        id: model.id,
        name: model.name,
        description: model.description ?? '',
        keywords: model.categories ?? [],
        dirPath: '',
        skillMdPath: '',
        source: 'organization',
        enabled: false,
        activation: DEFAULT_SKILL_ACTIVATION,
        hasScripts: false,
        hasReferences: false,
        visibility: 'organization',
        createdAt: Date.parse(model.createdDate) || 0,
        updatedAt: Date.parse(model.modifiedDate) || 0,
        remoteId: model.id,
        organizationId: model.organizationId,
        isOfficial: model.isOfficial,
    };
}

/** Map the API registration to the local McpServerModel shape the editor reads (non-secret config). */
function toLocalServer(reg: McpServerRegistrationModel): McpServerModel {
    return { ...registrationToCreateInput(reg), id: reg.id, displayName: reg.displayName } as unknown as McpServerModel;
}

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1.5rem;
`;

const SortRow = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 18rem), 1fr));
    gap: 0.75rem;
`;

const ExtensionCardView = styled(CardBorder)`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
`;

const CardDescription = styled(BodySmall)`
    color: ${(props) => props.theme.color.textBody};
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

/** Pinned to the bottom of the card, above the actions, where the hand-rolled stats row used to sit. */
const CardStats = styled(RatingSummary)`
    margin-top: auto;
`;

const CardActions = styled.div`
    display: flex;
    gap: 0.25rem;
    border-top: 1px solid ${(props) => props.theme.color.border};
    padding-top: 0.5rem;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 2.5rem 1rem;
    text-align: center;
    border: 1px dashed ${(props) => props.theme.color.border};
    border-radius: 0.5rem;
`;
